import axios from "utils/axios";

// PERFORMANCE: Global cache to prevent duplicate API calls
const profileCache = new Map();
const pendingRequests = new Map();

/**
 * Fetch complete candidate profile (unified source of truth)
 * Returns: { profile, sceneSetup, candidate, analyzeReports, avatarEvaluations, etc. }
 * PERFORMANCE: Uses global cache and request deduplication to prevent duplicate calls
 * @param {string} candidateId - Candidate ID
 * @param {string} [jobId] - Optional job ID for job-specific enrichment
 * @param {string} [requesterId] - Optional requester ID for connection data
 * @param {boolean} [forceRefresh=false] - Force refresh even if cached
 */
export const getCandidateProfile = (candidateId, jobId = null, requesterId = null, forceRefresh = false) => {
  // Create cache key
  const cacheKey = `${candidateId}-${jobId || ''}-${requesterId || ''}`;
  
  // Return cached result if available and not forcing refresh
  if (!forceRefresh && profileCache.has(cacheKey)) {
    const cached = profileCache.get(cacheKey);
    // Cache valid for 30 seconds
    if (Date.now() - cached.timestamp < 30000) {
      return Promise.resolve(cached.data);
    }
    profileCache.delete(cacheKey);
  }

  // Return pending request if one exists
  if (pendingRequests.has(cacheKey)) {
    return pendingRequests.get(cacheKey);
  }

  // Create new request
  const params = {};
  if (jobId) params.jobId = jobId;
  if (requesterId) params.requesterId = requesterId;

  const requestPromise = axios
    .get(`/candidate/${candidateId}/profile`, { params })
    .then((response) => {
      // Cache successful response
      profileCache.set(cacheKey, {
        data: response.data,
        timestamp: Date.now(),
      });
      pendingRequests.delete(cacheKey);
      return response.data;
    })
    .catch((error) => {
      pendingRequests.delete(cacheKey);
      console.error('❌ [getCandidateProfile] Failed to fetch profile:', error);
      throw error;
    });

  // Store pending request
  pendingRequests.set(cacheKey, requestPromise);
  
  return requestPromise;
};

/**
 * Update a single profile field
 * field: the field name to update (e.g., 'about_me', 'skills')
 * value: the new value for that field
 * isDraft: if true, won't mark as edited by user
 */
export const updateCandidateProfile = (candidateId, field, value, isDraft = false) =>
  new Promise((resolve, reject) => {
    axios
      .patch(`/candidate/${candidateId}/profile`, {
        field,
        value,
        isDraft,
      })
      .then((response) => {
        console.log(`✅ Field "${field}" updated:`, response.data);
        resolve(response.data);
      })
      .catch((error) => {
        console.error(`❌ Failed to update field "${field}":`, error);
        reject(error);
      });
  });

/**
 * Update multiple profile fields at once
 * fields: object with field names as keys and values as values
 * Example: { about_me: "New bio", skills: ["React", "Node"] }
 * isDraft: if true, won't mark as edited by user
 */
export const updateCandidateProfileBatch = (candidateId, fields, isDraft = false) =>
  new Promise((resolve, reject) => {
    axios
      .patch(`/candidate/${candidateId}/profile/batch`, {
        fields,
        isDraft,
      })
      .then((response) => {
        console.log(`✅ Batch update successful (${Object.keys(fields).length} fields):`, response.data);
        resolve(response.data);
      })
      .catch((error) => {
        console.error(`❌ Failed batch update:`, error);
        reject(error);
      });
  });

/**
 * Helper: Parse profile metadata to show user-friendly messages
 */
export const getProfileMetadataMessage = (metadata) => {
  if (!metadata) return '';

  const messages = [];

  if (metadata.lastParsedAt) {
    const lastParsed = new Date(metadata.lastParsedAt);
    const daysAgo = Math.floor((Date.now() - lastParsed) / (1000 * 60 * 60 * 24));
    if (daysAgo === 0) {
      messages.push('Resume parsed today');
    } else if (daysAgo === 1) {
      messages.push('Resume parsed yesterday');
    } else {
      messages.push(`Resume parsed ${daysAgo} days ago`);
    }
  }

  if (metadata.editsAfterParse && metadata.lastEditedAt) {
    const lastEdited = new Date(metadata.lastEditedAt);
    messages.push(`Last edited ${lastEdited.toLocaleDateString()}`);
  }

  return messages.join(' • ');
};

/**
 * Upload media file to SceneSetup S3 bucket
 * Used for experience media (images)
 * @param {FormData} formData - FormData with file, title, description
 * @returns {Promise} - Returns uploaded file data { link, key, title, description, imageSource, etc. }
 */
export const uploadSceneSetupMedia = (formData) =>
  new Promise((resolve, reject) => {
    axios
      .post('/scenesetup/media/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((response) => {
        console.log('✅ [uploadSceneSetupMedia] Media uploaded:', response.data);
        resolve(response.data);
      })
      .catch((error) => {
        console.error('❌ [uploadSceneSetupMedia] Failed to upload media:', error);
        reject(error);
      });
  });
